import { useDroppable } from "@dnd-kit/core";
import clsx from "clsx";
import { CSSProperties, FC, MouseEventHandler, PropsWithChildren } from "react";

import { useRideIsDroppable } from "@ryde/redux";
import { DragTypeEnum, TDragRideData, TDropRideData } from "@ryde/shared";

export interface IDroppableWrapper extends PropsWithChildren {
  id: string;
  disabled?: boolean;
  element?: "div" | "p" | "span" | "ul" | "tr" | "td";
  dropData?: TDropRideData;
  customClassName?: string;
  useMaxSize?: boolean;
  onClick?: MouseEventHandler<HTMLElement>;
}

export const DroppableRideWrapper: FC<IDroppableWrapper> = ({
  children,
  element,
  dropData,
  id,
  customClassName,
  disabled,
  onClick,
  useMaxSize = true,
}) => {
  const Element = element || "div";

  const { over, active, isOver, setNodeRef } = useDroppable({
    id,
    data: { ...dropData },
    disabled,
  });

  const dragItemData = active?.data?.current as TDragRideData | undefined;
  const overItemData = over?.data?.current as TDropRideData | undefined;
  const dropAccepted =
    !overItemData?.accepts?.length ||
    (dragItemData?.type && overItemData.accepts.includes(dragItemData.type));

  const { getIsDropTargetHighlighted, isRideDroppable } = useRideIsDroppable();
  const isOverHighlighted =
    Boolean(dropAccepted) &&
    getIsDropTargetHighlighted({
      dragItemData,
      overItemData,
      isOver,
      dropAccepted,
    });

  const style: CSSProperties = useMaxSize
    ? {
        width: "100%",
        height: "100%",
      }
    : {};

  const isRideGridKanbanDropNotAllowed =
    (dragItemData?.type === DragTypeEnum.KANBAN_RIDE ||
      dragItemData?.type === DragTypeEnum.GRID_RIDE ||
      dragItemData?.type === DragTypeEnum.GRID_PASSENGER ||
      dragItemData?.type === DragTypeEnum.KANBAN_PASSENGER ||
      dragItemData?.type === DragTypeEnum.CARD_BOARD_RIDE) &&
    active &&
    dropData &&
    !isRideDroppable({ dragSource: dragItemData, dropTarget: dropData })
      .success;

  return (
    <Element
      ref={setNodeRef}
      onClick={onClick}
      style={style}
      className={clsx({
        drag_over: isOverHighlighted,
        drag_not_allowed: isRideGridKanbanDropNotAllowed,
        ...(customClassName && { [customClassName]: true }),
      })}
    >
      {children}
    </Element>
  );
};
